import { globalEventBus, GameEvents } from '../core/EventBus.js';

/**
 * 计时器
 * 支持正计时和倒计时两种模式
 */
export class Timer {
    constructor(config = {}) {
        this.config = {
            enabled: config.enabled !== false,
            countDown: config.countDown || false,
            initialTime: config.initialTime || 0
        };

        this.events = globalEventBus;

        // 计时状态
        this.elapsed = 0;           // 已过时间（秒）
        this.remaining = this.config.initialTime;  // 剩余时间（秒）
        this.isRunning = false;
        this.isPaused = false;

        // 计时器ID
        this.intervalId = null;
        this.lastTick = 0;
    }

    /**
     * 开始计时
     */
    start() {
        if (!this.config.enabled || this.isRunning) return;

        this.isRunning = true;
        this.isPaused = false;
        this.lastTick = Date.now();

        this.intervalId = setInterval(() => this.tick(), 100);
    }

    /**
     * 计时器滴答
     */
    tick() {
        if (!this.isRunning || this.isPaused) return;

        const now = Date.now();
        const delta = (now - this.lastTick) / 1000;
        this.lastTick = now;

        if (this.config.countDown) {
            this.remaining = Math.max(0, this.remaining - delta);
            if (this.remaining <= 0) {
                this.stop();
                this.events.emit(GameEvents.TIMER_TICK, {
                    elapsed: this.elapsed,
                    remaining: 0,
                    isTimeUp: true
                });
                return;
            }
        } else {
            this.elapsed += delta;
        }

        this.events.emit(GameEvents.TIMER_TICK, {
            elapsed: this.elapsed,
            remaining: this.remaining,
            isTimeUp: false
        });
    }

    /**
     * 暂停计时
     */
    pause() {
        if (!this.isRunning) return;
        this.isPaused = true;
    }

    /**
     * 恢复计时
     */
    resume() {
        if (!this.isRunning || !this.isPaused) return;
        this.isPaused = false;
        this.lastTick = Date.now();
    }

    /**
     * 停止计时
     */
    stop() {
        this.isRunning = false;
        this.isPaused = false;

        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    /**
     * 重置计时器
     */
    reset() {
        this.stop();
        this.elapsed = 0;
        this.remaining = this.config.initialTime;
    }

    /**
     * 获取已过时间（秒）
     * @returns {number} 已过时间
     */
    getElapsed() {
        return Math.floor(this.elapsed);
    }

    /**
     * 获取已过时间（毫秒）
     * @returns {number} 已过时间（毫秒）
     */
    getElapsedMs() {
        return Math.floor(this.elapsed * 1000);
    }

    /**
     * 获取剩余时间（秒）
     * @returns {number} 剩余时间
     */
    getRemaining() {
        return Math.floor(this.remaining);
    }

    /**
     * 获取格式化的时间字符串
     * @param {boolean} useRemaining 是否使用剩余时间
     * @returns {string} 格式化时间 MM:SS
     */
    getFormattedTime(useRemaining = false) {
        const totalSeconds = useRemaining ? this.remaining : this.elapsed;
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = Math.floor(totalSeconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    /**
     * 获取详细格式化时间
     * @returns {string} HH:MM:SS
     */
    getDetailedFormattedTime() {
        const totalSeconds = this.elapsed;
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    /**
     * 设置倒计时时间
     * @param {number} seconds 秒数
     */
    setCountdownTime(seconds) {
        this.config.initialTime = seconds;
        this.remaining = seconds;
    }

    /**
     * 切换计时模式
     * @param {boolean} countDown 是否倒计时
     */
    setCountDownMode(countDown) {
        this.config.countDown = countDown;
    }

    /**
     * 是否正在运行
     * @returns {boolean} 是否运行中
     */
    isActive() {
        return this.isRunning && !this.isPaused;
    }

    /**
     * 销毁
     */
    destroy() {
        this.stop();
    }
}

export default Timer;
